import React, { useCallback, useEffect, useState } from "react";
import { api, asAbsolute } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, X, Save } from "lucide-react";
import FileUploader from "./FileUploader";

const empty = { image_url: "", caption: "", category: "", order: 100 };

export default function AdminGallery() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(null);
    const load = useCallback(() => api.get("/gallery").then((r) => setItems(r.data || [])), []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        try {
            const payload = { ...form, order: Number(form.order) || 100 };
            if (form.id) await api.put(`/gallery/${form.id}`, payload);
            else await api.post("/gallery", payload);
            toast.success("Saved");
            setForm(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete photo?")) return;
        await api.delete(`/gallery/${id}`);
        load();
    };

    return (
        <div data-testid="admin-gallery">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-semibold">Gallery</h1>
                    <p className="text-sm text-slate-500 mt-1">Upload photos of your work.</p>
                </div>
                <button onClick={() => setForm({ ...empty })} data-testid="gallery-add-btn" className="px-4 py-2 rounded-md bg-brand-forest text-white text-sm font-semibold inline-flex items-center gap-2"><Plus size={14} /> Add photo</button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {items.map((g) => (
                    <div key={g.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden group relative">
                        <div className="aspect-square overflow-hidden">
                            <img src={asAbsolute(g.image_url)} alt={g.caption} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-3">
                            <div className="text-xs text-brand-forest font-semibold">{g.category}</div>
                            <div className="text-sm truncate">{g.caption}</div>
                        </div>
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <button onClick={() => setForm({ ...g })} className="p-1.5 bg-white rounded shadow-sm text-xs">Edit</button>
                            <button onClick={() => remove(g.id)} className="p-1.5 bg-white rounded shadow-sm text-red-600"><Trash2 size={12} /></button>
                        </div>
                    </div>
                ))}
            </div>

            {form && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setForm(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg max-w-lg w-full">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200"><h2 className="font-semibold">{form.id ? "Edit photo" : "Add photo"}</h2><button onClick={() => setForm(null)}><X size={18} /></button></div>
                        <div className="p-5 space-y-3">
                            <FileUploader kind="image" current={form.image_url} label="Photo" onUploaded={(r) => setForm({ ...form, image_url: r.url })} />
                            <input placeholder="Or paste image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm" />
                            <input placeholder="Caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm" />
                            <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm" />
                            <input type="number" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm" />
                        </div>
                        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
                            <button onClick={() => setForm(null)} className="px-4 py-2 rounded text-sm">Cancel</button>
                            <button onClick={save} className="px-4 py-2 rounded bg-brand-forest text-white text-sm font-semibold inline-flex items-center gap-2"><Save size={14} /> Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
